import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getResidentInviteByToken, registerResident } from '../services/onboardingService'
import { API_BASE, GOOGLE_CLIENT_ID, IOS_CLIENT_ID } from '../config'
import { normalizeEmail, validatePersonName } from '../utils/validation'

const STEPS = { LOADING: 'loading', FORM: 'form', GOOGLE_PENDING: 'google_pending', DONE: 'done', ERROR: 'error' }

export default function ResidentRegister() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [step, setStep] = useState(STEPS.LOADING)
  const [invite, setInvite] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Load invite details from token
  useEffect(() => {
    if (!token) {
      setStep(STEPS.ERROR)
      setError('Invalid invite link — no token found.')
      return
    }
    getResidentInviteByToken(token)
      .then(data => {
        if (data.STATUS === 'REGISTERED') {
          setStep(STEPS.ERROR)
          setError('This invite has already been used. Please contact your community admin.')
          return
        }
        setInvite(data)
        setFirstName(data.FIRST_NAME || '')
        setLastName(data.LAST_NAME || '')
        setStep(STEPS.FORM)
      })
      .catch(err => {
        setStep(STEPS.ERROR)
        setError(err.message || 'Invalid or expired invite link.')
      })
  }, [token])

  // Handle Google OAuth return with pending registration
  useEffect(() => {
    const pendingRaw = sessionStorage.getItem('pending_resident_registration')
    if (!pendingRaw) return
    const hash = window.location.hash.replace(/^#/, '')
    const params = new URLSearchParams(hash)
    const idToken = params.get('id_token')
    if (!idToken) return

    // We have both a pending registration and a fresh Google token
    ;(async () => {
      try {
        const pending = JSON.parse(pendingRaw)
        sessionStorage.removeItem('pending_resident_registration')

        // Decode email from idToken
        const parts = idToken.split('.')
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
        const googleEmail = normalizeEmail(payload.email || '')

        if (googleEmail !== pending.email.toLowerCase()) {
          setStep(STEPS.ERROR)
          setError(`Email mismatch: you signed in as ${googleEmail} but the invite is for ${pending.email}. Please use the correct Google account.`)
          return
        }

        setLoading(true)
        setStep(STEPS.GOOGLE_PENDING)

        await registerResident({
          token: pending.token,
          email: googleEmail,
          firstName: pending.firstName,
          lastName: pending.lastName,
        })

        // Store token for auto-login
        try { sessionStorage.setItem('id_token', idToken) } catch (e) {}

        setStep(STEPS.DONE)
      } catch (err) {
        setStep(STEPS.ERROR)
        setError(err.message || 'Registration failed. Please try again.')
      } finally {
        setLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleContinueWithGoogle(e) {
    e.preventDefault()
    setError('')
    if (!validatePersonName(firstName)) {
      setError('Please enter a valid first name')
      return
    }
    if (lastName.trim() && !validatePersonName(lastName)) {
      setError('Please enter a valid last name')
      return
    }
    if (!invite) return

    // Store pending registration details in sessionStorage before Google redirect
    const pending = {
      token,
      email: invite.EMAIL,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    }
    try {
      sessionStorage.setItem('pending_resident_registration', JSON.stringify(pending))
    } catch (e) { /* ignore */ }

    // Redirect to Google OAuth
    const isCapacitorNative = !!(window.Capacitor?.isNativePlatform?.())
    const CLIENT_ID = isCapacitorNative ? IOS_CLIENT_ID : GOOGLE_CLIENT_ID
    const redirectUri = isCapacitorNative
      ? 'http://localhost/oauth2/callback'
      : `${window.location.origin}/resident-register`
    const nonce = Math.random().toString(36).substring(2)
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
      login_hint: invite.EMAIL,
    })
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (step === STEPS.LOADING) {
    return <LoadingScreen message="Loading your invite…" />
  }

  if (step === STEPS.GOOGLE_PENDING) {
    return <LoadingScreen message="Completing registration…" />
  }

  if (step === STEPS.ERROR) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={{ ...styles.icon, background: '#fef2f2', color: '#dc2626' }}>✕</div>
          <h2 style={styles.title}>Invite Error</h2>
          <p style={styles.hint}>{error}</p>
          <button style={styles.btn} onClick={() => navigate('/')}>Go to Sign In</button>
        </div>
      </div>
    )
  }

  if (step === STEPS.DONE) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={{ ...styles.icon, background: '#dcfce7', color: '#16a34a' }}>✓</div>
          <h2 style={styles.title}>Registration Complete!</h2>
          <p style={styles.hint}>
            Welcome, <strong>{firstName}</strong>! Your account has been created. Sign in to access your resident portal.
          </p>
          <button style={styles.btn} onClick={() => navigate('/')}>Sign In</button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoCircle}>
            <img src="/apartment-icon.svg" alt="" width={36} height={36} />
          </div>
          <h1 style={styles.brand}>Smart Community</h1>
          <p style={styles.tagline}>Resident Registration</p>
        </div>

        {invite && (
          <div style={styles.inviteBadge}>
            🏠 You've been invited to join <strong>{invite.COMMUNITY_ID}</strong>
            {invite.UNIT_NBR ? ` · Unit ${invite.UNIT_NBR}` : ''}
          </div>
        )}

        <form onSubmit={handleContinueWithGoogle} style={styles.form}>
          <label style={styles.label}>Email Address</label>
          <input
            style={{ ...styles.input, background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }}
            type="email"
            value={invite?.EMAIL || ''}
            readOnly
            disabled
            autoComplete="email"
          />
          <p style={styles.hint}>This email matches your invite. You must sign in with this Google account.</p>

          <div style={styles.row}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>First Name *</label>
              <input
                style={styles.input}
                placeholder="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </div>
            <div style={{ width: 12 }} />
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Last Name</label>
              <input
                style={styles.input}
                placeholder="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.btnGoogle} disabled={loading}>
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="" width={20} height={20}
              style={{ marginRight: 10 }}
            />
            {loading ? 'Please wait…' : 'Continue with Google'}
          </button>

          <p style={styles.legalHint}>
            Your email will be verified against this invite. Registration requires a Google account
            matching <strong>{invite?.EMAIL}</strong>.
          </p>
        </form>
      </div>
    </div>
  )
}

function LoadingScreen({ message }) {
  return (
    <div style={{ ...styles.root, flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 32, animation: 'spin 1s linear infinite' }}>⟳</div>
      <p style={{ color: '#fff', fontSize: 15 }}>{message}</p>
    </div>
  )
}

const styles = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #3b82f6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '32px 28px',
    width: '100%',
    maxWidth: 440,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  header: { textAlign: 'center', marginBottom: 20 },
  logoCircle: {
    width: 60, height: 60, borderRadius: '50%', background: '#eff6ff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
  },
  brand: { fontSize: 20, fontWeight: 700, color: '#1d4ed8', margin: 0 },
  tagline: { fontSize: 13, color: '#6b7280', margin: '4px 0 0' },
  inviteBadge: {
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 20,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 13, fontWeight: 500, color: '#374151' },
  input: {
    padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  row: { display: 'flex', gap: 0 },
  hint: { fontSize: 12, color: '#6b7280', margin: '2px 0 4px', lineHeight: 1.4 },
  legalHint: { fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 8, lineHeight: 1.5 },
  error: {
    fontSize: 13, color: '#dc2626', background: '#fef2f2',
    padding: '8px 12px', borderRadius: 6, margin: '4px 0',
  },
  btn: {
    marginTop: 8, padding: '12px 16px', background: '#1d4ed8', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
    cursor: 'pointer', width: '100%',
  },
  btnGoogle: {
    marginTop: 12, padding: '12px 16px',
    background: '#fff', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  icon: {
    fontSize: 32, width: 72, height: 72, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 16px',
  },
  title: { textAlign: 'center', fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 8px' },
}
